import React, { useEffect, useState } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useNotification } from '../../contexts/NotificationContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import campaignService from '../../utils/campaignService';

const CampaignSetup = ({ data, onSave, onSkip, onBack, isLoading }) => {
  const { currentOrganization, updateOrganization } = useOrganization();
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email_title: 'How was our service?',
    email_subject: 'How was your experience with {{business.name}}?',
    email_body_template: `Hi {{contact.name}},\n\nWe hope you enjoyed your recent experience with {{business.name}}. \n\nYour feedback helps us continue to improve our services and provide the best possible experience for all our customers.\n\nCould you take a moment to share your thoughts?`,
    cta_button_text: 'Leave us a review',
    positive_threshold: 4,
    selected_review_platform: 'google',
    platform_urls: { google: '', yelp: '', facebook: '' },
    ...data
  });

  // Prefill from existing campaign and organization settings
  useEffect(() => {
    const loadExisting = async () => {
      if (!currentOrganization?.id) return;
      try {
        setLoading(true);
        
        // First, get organization's platform URLs as defaults
        const orgPlatformUrls = {
          google: currentOrganization.google_review_url || '',
          yelp: currentOrganization.yelp_url || '',
          facebook: currentOrganization.facebook_url || ''
        };
        
        // Then check for existing campaign
        const existing = await campaignService.getCampaign(currentOrganization.id);
        if (existing) {
          setForm(prev => ({
            ...prev,
            email_title: existing.email_title || prev.email_title,
            email_subject: existing.email_subject || prev.email_subject,
            email_body_template: existing.email_body_template || prev.email_body_template,
            cta_button_text: existing.cta_button_text || prev.cta_button_text,
            positive_threshold: existing.positive_threshold || prev.positive_threshold,
            selected_review_platform: existing.selected_review_platform || currentOrganization.primary_review_platform || prev.selected_review_platform,
            platform_urls: existing.platform_urls || orgPlatformUrls
          }));
        } else {
          // No existing campaign, use organization defaults
          setForm(prev => ({
            ...prev,
            selected_review_platform: currentOrganization.primary_review_platform || prev.selected_review_platform,
            platform_urls: orgPlatformUrls
          }));
        }
      } catch (err) {
        console.warn('Failed to prefill campaign for onboarding:', err);
      } finally {
        setLoading(false);
      }
    };
    loadExisting();
  }, [currentOrganization?.id, currentOrganization?.google_review_url, currentOrganization?.yelp_url, currentOrganization?.facebook_url, currentOrganization?.primary_review_platform]);

  const handleSave = async () => {
    if (!currentOrganization?.id) {
      addNotification('No organization found for campaign setup', 'error');
      return;
    }
    try {
      setLoading(true);
      const toSave = {
        email_title: form.email_title,
        email_subject: form.email_subject,
        email_body_template: form.email_body_template,
        cta_button_text: form.cta_button_text,
        positive_threshold: form.positive_threshold,
        selected_review_platform: form.selected_review_platform,
        platform_urls: form.platform_urls
      };
      
      // Save campaign
      const saved = await campaignService.upsertCampaign(currentOrganization.id, toSave);
      
      // Also sync platform URLs back to organization settings
      const orgUpdates = {
        google_review_url: form.platform_urls.google || '',
        yelp_url: form.platform_urls.yelp || '',
        facebook_url: form.platform_urls.facebook || '',
        primary_review_platform: form.selected_review_platform
      };
      
      // Update organization with new platform settings
      await updateOrganization(orgUpdates);
      
      addNotification('Campaign settings saved', 'success');
      onSave({ campaignSetup: toSave, campaignId: saved?.id || null });
    } catch (err) {
      console.error('Error saving campaign during onboarding:', err);
      addNotification('Failed to save campaign. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to replace variables for preview
  const replaceVariablesForPreview = (template) => {
    if (!template) return '';
    return template
      .replace(/\{\{contact\.name\}\}/g, 'John Doe')
      .replace(/\{\{business\.name\}\}/g, currentOrganization?.name || 'Your Business')
      .replace(/\{\{service\.name\}\}/g, 'Service');
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="text-center mb-2">
        <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Mail" size={24} className="text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">Basic Campaign Setup</h2>
        <p className="text-text-secondary">Configure the key settings for your review request emails.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left side - Form */}
        <div className="space-y-4">
          <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Email title</label>
          <Input
            value={form.email_title}
            onChange={(e) => setForm(prev => ({ ...prev, email_title: e.target.value }))}
            placeholder="How was our service?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Email subject</label>
          <Input
            value={form.email_subject}
            onChange={(e) => setForm(prev => ({ ...prev, email_subject: e.target.value }))}
            placeholder="How was your experience with {{business.name}}?"
          />
          <p className="text-xs text-text-muted mt-1">Supports variables: {"{{contact.name}}"}, {"{{business.name}}"}.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Email body</label>
          <textarea
            value={form.email_body_template}
            onChange={(e) => setForm(prev => ({ ...prev, email_body_template: e.target.value }))}
            rows={6}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="Body content with variables"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">CTA button text</label>
            <Input
              value={form.cta_button_text}
              onChange={(e) => setForm(prev => ({ ...prev, cta_button_text: e.target.value }))}
              placeholder="Leave us a review"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Positive rating threshold</label>
            <select
              value={form.positive_threshold}
              onChange={(e) => setForm(prev => ({ ...prev, positive_threshold: Number(e.target.value) }))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value={3}>3+ stars for positive reviews</option>
              <option value={4}>4+ stars for positive reviews</option>
              <option value={5}>5 stars only for positive reviews</option>
            </select>
          </div>
        </div>

        {/* Platform Selection */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">Where should customers leave reviews?</label>
          <div className="space-y-3">
            {/* Google */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">G</span>
                </div>
                <div>
                  <div className="font-medium text-text-primary">Google Business Profile</div>
                  <div className="text-sm text-text-secondary">Recommended - highest impact</div>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="review_platform"
                  checked={form.selected_review_platform === 'google'}
                  onChange={() => setForm(prev => ({ ...prev, selected_review_platform: 'google' }))}
                  className="text-primary focus:ring-primary mr-2"
                />
              </label>
            </div>

            {/* Yelp */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">Y</span>
                </div>
                <div>
                  <div className="font-medium text-text-primary">Yelp</div>
                  <div className="text-sm text-text-secondary">Great for restaurants & services</div>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="review_platform"
                  checked={form.selected_review_platform === 'yelp'}
                  onChange={() => setForm(prev => ({ ...prev, selected_review_platform: 'yelp' }))}
                  className="text-primary focus:ring-primary mr-2"
                />
              </label>
            </div>

            {/* Facebook */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">F</span>
                </div>
                <div>
                  <div className="font-medium text-text-primary">Facebook</div>
                  <div className="text-sm text-text-secondary">Social media reviews</div>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="review_platform"
                  checked={form.selected_review_platform === 'facebook'}
                  onChange={() => setForm(prev => ({ ...prev, selected_review_platform: 'facebook' }))}
                  className="text-primary focus:ring-primary mr-2"
                />
              </label>
            </div>
          </div>

          {/* URL Input for Selected Platform */}
          {form.selected_review_platform && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                {form.selected_review_platform === 'google' && 'Google Business Profile URL'}
                {form.selected_review_platform === 'yelp' && 'Yelp Business Page URL'}
                {form.selected_review_platform === 'facebook' && 'Facebook Page URL'}
              </label>
              <Input
                value={form.platform_urls[form.selected_review_platform] || ''}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  platform_urls: {
                    ...prev.platform_urls,
                    [form.selected_review_platform]: e.target.value
                  }
                }))}
                placeholder={
                  form.selected_review_platform === 'google' 
                    ? 'https://g.page/your-business/review'
                    : form.selected_review_platform === 'yelp'
                    ? 'https://www.yelp.com/biz/your-business'
                    : 'https://www.facebook.com/your-business/reviews'
                }
              />
              <p className="text-xs text-text-muted mt-1">
                Optional: Add your review page URL to direct customers to the right place.
              </p>
            </div>
          )}
          </div>

          {/* Why connect platforms info */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Info" size={14} className="text-white" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-2">Why connect your review platforms?</h4>
                <p className="text-sm text-blue-800">
                  ServiceReviewAI can help direct customers to the right review platform, track your online 
                  reputation, and send targeted review requests to improve your ratings across all platforms.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Email Preview */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-text-primary text-center">Email Preview</h3>
          
          {/* Email Preview */}
          <div className="bg-background rounded-lg border border-border shadow-sm overflow-hidden sticky top-4">
            {/* Email Header */}
            <div className="bg-surface-hover px-4 py-3 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="text-sm font-medium text-text-primary">
                    {replaceVariablesForPreview(form.email_subject) || `How was your experience with ${currentOrganization?.name || 'Your Business'}?`}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {currentOrganization?.name || 'Your Business'} &lt;noreply@reviews.servicereview.ai&gt;
                  </div>
                </div>
              </div>
            </div>
            
            {/* Email Body */}
            <div className="p-6 bg-background">
              {/* Email Title */}
              <h1 className="text-xl font-semibold text-text-primary mb-4">
                {replaceVariablesForPreview(form.email_title) || 'How was our service?'}
              </h1>
              
              <div className="mb-4">
                <div className="whitespace-pre-line text-text-secondary mb-4">
                  {replaceVariablesForPreview(form.email_body_template)}
                </div>
              </div>
              
              {/* CTA Button */}
              <div className="text-center">
                <div className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium">
                  {form.cta_button_text || "Leave us a review"}
                </div>
              </div>
              
              {/* Preview info */}
              <div className="mt-6 pt-4 border-t border-border">
                <div className="text-xs text-text-muted text-center">
                  <p>Rating threshold: {form.positive_threshold}+ stars for positive reviews</p>
                  <p>Platform: {form.selected_review_platform ? 
                    (form.selected_review_platform === 'google' ? 'Google Business Profile' : 
                     form.selected_review_platform === 'yelp' ? 'Yelp' : 'Facebook') 
                    : 'Not selected'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center pt-4">
        <Button variant="ghost" onClick={onBack} disabled={isLoading || loading}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Go Back
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onSkip} disabled={isLoading || loading}>Skip for now</Button>
          <Button onClick={handleSave} disabled={isLoading || loading}>
            {isLoading || loading ? 'Saving…' : 'Save and continue'}
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignSetup;


